import { FC, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { Button, Card, FancyAnchorLink, Label, Title, Phone } from "shared/components";
import { ICardTheme, ITheme } from "shared/types";
import { BoldText, FlexContainer, Icon, MutedText, IconWithText } from "shared/styles";
import { Cake, Mail, Phone as PhoneIcon } from "shared/assets/icons";
import { Spacer } from "shared/layout";
import { copy } from "shared/assets";

import { ChangePassword, ChangeEmailRequest } from "Auth/containers";

import { getProfileSummaryActions } from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import { useFullUserProfileContext } from "Bands/contexts";
import { useProfileContext } from "Auth/contexts";

const {
  auth,
  profile: { profileIds },
} = copy;

interface ISummaryItem {
  id: string;
  icon: React.ComponentType<any>;
  item: JSX.Element;
}

interface Props {
  showLogout?: boolean;
  canManageProfile?: boolean;
}

export const ProfileSummary: FC<Props> = ({ showLogout, canManageProfile }) => {
  const history = useHistory();

  const { handleLogout } = useProfileContext();
  const { userName, email, formattedBirthDate, nationalityCountry, phoneNumbers } =
    useFullUserProfileContext();

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const summaryItems: ISummaryItem[] = useMemo(() => {
    const items = [
      {
        id: "mail",
        icon: Mail,
        item: (
          <FancyAnchorLink href={`mailto:${email}`}>
            <span>{email}</span>
          </FancyAnchorLink>
        ),
      },
    ];

    if (phoneNumbers?.[0]?.phoneNumber) {
      items.push({
        id: "phoneNumber",
        icon: PhoneIcon,
        item: <Phone phone={phoneNumbers?.[0].phoneNumber} />,
      });
    }

    if (formattedBirthDate) {
      items.push({
        id: "birthdate",
        icon: Cake,
        item: (
          <BoldText>
            <MutedText>{formattedBirthDate}</MutedText>
          </BoldText>
        ),
      });
    }

    return items;
  }, [email, phoneNumbers, formattedBirthDate]);

  const handleEdit = useCallback(() => {
    history.push(BAND_PATHS.EDIT_PROFILE);
  }, [history]);

  const actions = useMemo(() => {
    if (!canManageProfile) return [];

    return getProfileSummaryActions({
      handleEdit,
      handleChangeEmail: () => setShowChangeEmail(true),
      handleChangePassword: () => setShowChangePassword(true),
    });
  }, [canManageProfile, handleEdit]);

  return (
    <Card
      id={profileIds.personal}
      theme={ICardTheme.header}
      actions={actions}
      actionDataTestId="profile_summary_actions"
    >
      <Spacer size="s">
        <Spacer spaceDirection="horizontal" align="center" size="s">
          <Title title={userName} asComponent="h2" />
          {showLogout && (
            <Button onClick={handleLogout} size="s" theme={ITheme.secondary}>
              {auth.logout}
            </Button>
          )}
        </Spacer>
        {nationalityCountry && (
          <IconWithText>
            {nationalityCountry?.flag}
            {nationalityCountry?.name && <Label>{nationalityCountry?.name}</Label>}
          </IconWithText>
        )}
        <div>
          <FlexContainer>
            {summaryItems.map(({ id, icon, item }) => (
              <IconWithText key={id}>
                <Icon as={icon} />
                {item}
              </IconWithText>
            ))}
          </FlexContainer>
        </div>
      </Spacer>
      {showChangePassword && <ChangePassword handleClose={() => setShowChangePassword(false)} />}
      {showChangeEmail && email && (
        <ChangeEmailRequest oldEmail={email} handleClose={() => setShowChangeEmail(false)} />
      )}
    </Card>
  );
};
